import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { CATEGORIES } from "../mock/mock";
import { useProducts } from "../context/ProductsContext";
import ProductCard from "../components/ProductCard";
import { Filter, X, ChevronDown, Home } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ShopPage = () => {
  const { products } = useProducts();
  const location = useLocation();
  const nav = useNavigate();

  // Re-parse the URL every time location.search changes so category & query stay in sync
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlCat = params.get("cat") || "all";
  const urlQ = params.get("q") || "";

  const [cat, setCat] = useState(urlCat);
  const [sort, setSort] = useState("popular");
  const [priceRange, setPriceRange] = useState("all");
  const [search, setSearch] = useState(urlQ);
  const [mobileFilter, setMobileFilter] = useState(false);

  // Sync state when the URL changes (e.g. clicking a nav link while already on /shop)
  useEffect(() => {
    setCat(urlCat);
    setSearch(urlQ);
    // reset filters/scroll when the category changes
    setPriceRange("all");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [urlCat, urlQ]);

  const catOptions = [
    { id: "all", name: "All Products" },
    ...CATEGORIES,
    { id: "seeds", name: "Seeds" },
    { id: "berries", name: "Berries" },
    { id: "breakfast", name: "Breakfast" },
    { id: "combos", name: "Combos & Gifts" },
    { id: "snacks", name: "Snacks" },
    { id: "jumbo", name: "Jumbo Nuts" },
    { id: "figs", name: "Figs" },
    { id: "apricots", name: "Apricots" },
    { id: "makhana", name: "Makhana" },
  ];
  const uniqueCats = Array.from(new Map(catOptions.map((c) => [c.id, c])).values());

  const filtered = useMemo(() => {
    let list = [...products];
    if (cat !== "all") {
      list = list.filter((p) => p.category === cat || p.subcategory === cat);
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(s) || p.description.toLowerCase().includes(s));
    }
    if (priceRange !== "all") {
      const [lo, hi] = priceRange.split("-").map(Number);
      list = list.filter((p) => p.price >= lo && p.price <= hi);
    }
    if (sort === "low") list.sort((a, b) => a.price - b.price);
    else if (sort === "high") list.sort((a, b) => b.price - a.price);
    else if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    else if (sort === "discount") list.sort((a, b) => b.discount - a.discount);
    return list;
  }, [cat, sort, priceRange, search, products]);

  const activeCatName = uniqueCats.find((c) => c.id === cat)?.name || "All Products";

  return (
    <main className="bg-cream min-h-screen">
      {/* Breadcrumb / hero */}
      <div className="bg-[#f3ecda] border-b border-[#efe4c9]">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-10">
          <div className="flex items-center gap-2 text-[12.5px] text-[#6b4f39] mb-2">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-brand"><Home className="h-3.5 w-3.5" /> Home</Link>
            <span>/</span>
            <span className="text-brand font-semibold">Shop</span>
          </div>
          <h1 className="font-serif-brand text-[32px] md:text-[44px] font-bold text-[#2a1a15]">{activeCatName}</h1>
          <p className="mt-1 text-[14.5px] text-[#6b4f39]">Premium quality dry fruits, nuts, seeds & more — handpicked with care.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-8 grid md:grid-cols-[240px_1fr] lg:grid-cols-[260px_1fr] gap-6 md:gap-8">
        {/* Sidebar */}
        <aside className={`${mobileFilter ? "fixed inset-0 z-50 bg-black/50 md:static md:bg-transparent" : "hidden"} md:block`}>
          <div className={`${mobileFilter ? "absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-cream p-5 overflow-y-auto" : ""} md:bg-transparent md:p-0 md:static md:w-auto`}>
            <div className="md:hidden flex items-center justify-between mb-4">
              <div className="font-serif-brand font-bold text-lg">Filters</div>
              <button onClick={() => setMobileFilter(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="bg-white rounded-xl border border-[#efe4c9] p-4">
              <div className="font-serif-brand font-bold text-[15px] text-[#2a1a15] mb-3">Category</div>
              <ul className="space-y-1.5">
                {uniqueCats.map((c) => (
                  <li key={c.id}>
                    <button onClick={() => { nav(c.id === "all" ? "/shop" : `/shop?cat=${c.id}`); setMobileFilter(false); }} className={`w-full text-left text-[13.5px] px-2.5 py-1.5 rounded-md transition-colors ${cat === c.id ? "bg-brand text-white" : "text-[#3a2a20] hover:bg-[#f9f1de]"}`}>{c.name}</button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-xl border border-[#efe4c9] p-4 mt-4">
              <div className="font-serif-brand font-bold text-[15px] text-[#2a1a15] mb-3">Price Range</div>
              <ul className="space-y-1.5">
                {[
                  { id: "all", name: "All Prices" },
                  { id: "0-300", name: "Under ₹300" },
                  { id: "300-600", name: "₹300 – ₹600" },
                  { id: "600-1000", name: "₹600 – ₹1000" },
                  { id: "1000-99999", name: "Above ₹1000" },
                ].map((r) => (
                  <li key={r.id}>
                    <button onClick={() => setPriceRange(r.id)} className={`w-full text-left text-[13.5px] px-2.5 py-1.5 rounded-md transition-colors ${priceRange === r.id ? "bg-olive text-white" : "text-[#3a2a20] hover:bg-[#f9f1de]"}`}>{r.name}</button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#2a1a15] rounded-xl p-4 mt-4 text-white">
              <div className="font-serif-brand text-lg">Corporate Gifting</div>
              <p className="text-[13px] text-[#e5d3ac] mt-1.5">Elegant, customized hampers for your team & clients.</p>
              <Link to="/contact" className="mt-3 inline-block text-gold text-[13px] font-semibold hover:underline">Enquire now →</Link>
            </div>
          </div>
        </aside>

        {/* Grid */}
        <div>
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="text-[13.5px] text-[#6b4f39]">Showing <span className="font-semibold text-[#2a1a15]">{filtered.length}</span> products</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setMobileFilter(true)} className="md:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e3d3ad] bg-white text-[13px]">
                <Filter className="h-4 w-4" /> Filters
              </button>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[170px] h-10 bg-white border-[#e3d3ad]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popular">Most Popular</SelectItem>
                  <SelectItem value="low">Price: Low to High</SelectItem>
                  <SelectItem value="high">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Top Rated</SelectItem>
                  <SelectItem value="discount">Best Discount</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-[#efe4c9] p-10 text-center">
              <div className="font-serif-brand text-2xl text-[#2a1a15]">No products found</div>
              <p className="text-[#6b4f39] mt-2 text-sm">Try changing your filters or search.</p>
              <button onClick={() => { nav("/shop"); setPriceRange("all"); }} className="mt-4 bg-brand hover:bg-[#6c1414] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors">Reset Filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default ShopPage;
